import { GameState, TeamInLeague, AssistantManagerBriefing } from '../domain/entities';
import { ASSISTANT_MANAGER_LEVELS } from '../domain/constants';

export class AssistantManagerBriefingService {
    /**
     * Generate an initial briefing when starting a new game, season, or joining a club
     */
    static generateInitialBriefing(gameState: GameState, playerTeam: TeamInLeague, league: any, occasion: 'game_start' | 'season_start' | 'club_switch'): AssistantManagerBriefing {
        const managerLevel = ASSISTANT_MANAGER_LEVELS[playerTeam.assistantManager.level - 1];

        const briefing: AssistantManagerBriefing = {
            season: gameState.season,
            week: gameState.currentWeek,
            data: {
                status: this.generateStatus(playerTeam, league),
                keyIssues: this.generateKeyIssues(playerTeam, league, managerLevel.accuracy),
                squadCondition: this.generateSquadCondition(playerTeam),
                injuryReport: this.generateInjuryReport(playerTeam),
                formWatch: this.generateFormWatch(playerTeam),
                topFormers: this.getTopFormers(playerTeam),
                playerAlerts: this.generatePlayerAlerts(playerTeam, managerLevel.accuracy),
                finances: this.generateFinances(playerTeam),
                tacticalRecommendations: this.generateTacticalRecommendations(playerTeam, managerLevel.accuracy),
                transferTargets: managerLevel.accuracy >= 0.7 ? this.generateTransferTargets(playerTeam, managerLevel.accuracy) : undefined,
                upcomingFixtures: managerLevel.accuracy >= 0.8 ? this.generateFixtureDifficulty(gameState) : undefined,
                advice: this.getManagerAdvice(playerTeam, managerLevel.accuracy),
            }
        };

        return briefing;
    }

    /**
     * Generate a weekly briefing from the assistant manager
     */
    static generateWeeklyBriefing(gameState: GameState, playerTeam: TeamInLeague, league: any): AssistantManagerBriefing {
        const managerLevel = ASSISTANT_MANAGER_LEVELS[playerTeam.assistantManager.level - 1];

        const briefing: AssistantManagerBriefing = {
            season: gameState.season,
            week: gameState.currentWeek,
            data: {
                status: this.generateStatus(playerTeam, league),
                keyIssues: this.generateKeyIssues(playerTeam, league, managerLevel.accuracy),
                squadCondition: this.generateSquadCondition(playerTeam),
                injuryReport: this.generateInjuryReport(playerTeam),
                formWatch: this.generateFormWatch(playerTeam),
                topFormers: this.getTopFormers(playerTeam),
                playerAlerts: this.generatePlayerAlerts(playerTeam, managerLevel.accuracy),
                finances: this.generateFinances(playerTeam),
                tacticalRecommendations: this.generateTacticalRecommendations(playerTeam, managerLevel.accuracy),
                transferTargets: managerLevel.accuracy >= 0.7 ? this.generateTransferTargets(playerTeam, managerLevel.accuracy) : undefined,
                upcomingFixtures: managerLevel.accuracy >= 0.8 ? this.generateFixtureDifficulty(gameState) : undefined,
                advice: this.getManagerAdvice(playerTeam, managerLevel.accuracy),
            }
        };

        return briefing;
    }

    private static generateStatus(team: TeamInLeague, league: any): string {
        const teamPosition = league.teams.findIndex((t: any) => t.name === team.name) + 1;
        const leagueSize = league.teams.length;
        const pointsFromLeader = league.teams[0].points - team.points;
        const pointsFromRelegation = team.points - league.teams[leagueSize - 1].points;

        const recentWins = team.squad.reduce((sum, p) => {
            return sum + (p.recentRatings ? p.recentRatings.slice(-3).filter(r => r >= 7).length : 0);
        }, 0);
        const momentum = recentWins > (team.squad.length * 0.4) ? 'upward' : 'downward';

        if (teamPosition === 1) {
            return `Leading ${leagueSize}-team league. ${pointsFromLeader > 0 ? `${pointsFromLeader}pts ahead of 2nd` : 'Close competition'}. Momentum: ${momentum}.`;
        } else if (teamPosition <= Math.ceil(leagueSize * 0.25)) {
            return `${this.getOrdinal(teamPosition)} place (${pointsFromLeader}pts from top). Top 4 within reach. Momentum: ${momentum}.`;
        } else if (teamPosition <= Math.ceil(leagueSize * 0.5)) {
            const promotionSpots = Math.ceil(leagueSize * 0.25);
            const ptsToPromotion = league.teams[promotionSpots - 1].points - team.points;
            return `${this.getOrdinal(teamPosition)} place. ${ptsToPromotion}pts to promotion zone. Momentum: ${momentum}.`;
        } else if (teamPosition >= leagueSize - Math.ceil(leagueSize * 0.25)) {
            const ptsToSafety = league.teams[leagueSize - Math.ceil(leagueSize * 0.25) - 1].points - team.points;
            return `${this.getOrdinal(teamPosition)} place (RELEGATION ZONE). ${ptsToSafety}pts to safety. URGENT: ${momentum}.`;
        } else {
            const ptsToPromotion = league.teams[Math.ceil(leagueSize * 0.25) - 1].points - team.points;
            return `${this.getOrdinal(teamPosition)} place. ${ptsToPromotion}pts to promotion spots. Momentum: ${momentum}.`;
        }
    }

    private static generateKeyIssues(team: TeamInLeague, league: any, accuracy: number): Array<{issue: string; severity: 'critical' | 'warning' | 'info'; icon: string}> {
        const issues: Array<{issue: string; severity: 'critical' | 'warning' | 'info'; icon: string}> = [];

        // Injury issues
        const severeInjuries = team.squad.filter(p => p.injury?.type === 'Severe').length;
        if (severeInjuries > 2) {
            issues.push({issue: `${severeInjuries} long-term injuries impacting team strength`, severity: 'critical', icon: 'fa-ambulance'});
        } else if (severeInjuries > 0) {
            issues.push({issue: `${severeInjuries} player(s) out long-term`, severity: 'warning', icon: 'fa-ambulance'});
        }

        // Fitness issues
        const exhaustedPlayers = team.squad.filter(p => p.fitness < 30).length;
        if (exhaustedPlayers > 3) {
            issues.push({issue: `${exhaustedPlayers} players dangerously fatigued - rotation needed immediately`, severity: 'critical', icon: 'fa-battery-empty'});
        } else if (exhaustedPlayers > 0) {
            issues.push({issue: `Squad fatigue affecting performance`, severity: 'warning', icon: 'fa-battery-quarter'});
        }

        // Morale issues
        const furiousPlayers = team.squad.filter(p => p.morale === 'Furious').length;
        const unhappyPlayers = team.squad.filter(p => p.morale === 'Unhappy').length;
        if (furiousPlayers > 0) {
            issues.push({issue: `${furiousPlayers} player(s) furious - at risk of transfer request or form collapse`, severity: 'critical', icon: 'fa-face-flushed'});
        } else if (unhappyPlayers > 2) {
            issues.push({issue: `${unhappyPlayers} players unhappy - morale interventions needed`, severity: 'warning', icon: 'fa-face-sad-tear'});
        }

        // Form issues
        const poorForm = team.squad.filter(p => (p.form || 0) < 1).length;
        if (poorForm > team.squad.length * 0.3) {
            issues.push({issue: `${poorForm} players in poor form - tactical adjustments needed`, severity: 'warning', icon: 'fa-trending-down'});
        }

        // Financial issues
        if (team.finances.balance < 0) {
            issues.push({issue: `In deficit - no transfer spending possible`, severity: 'critical', icon: 'fa-money-bill-wave'});
        } else if (team.finances.balance < 500000) {
            issues.push({issue: `Low funds - limited transfer options`, severity: 'warning', icon: 'fa-coins'});
        }

        // Tactical issues (higher accuracy managers detect these)
        if (accuracy >= 0.8) {
            const avgGoalsAgainst = team.goalsAgainst / Math.max(1, team.played);
            const avgGoalsFor = team.goalsFor / Math.max(1, team.played);

            if (avgGoalsAgainst > 1.8) {
                issues.push({issue: `Defense is leaky (${avgGoalsAgainst.toFixed(1)} GA/game) - consider defensive formation`, severity: 'warning', icon: 'fa-shield-halved'});
            }
            if (avgGoalsFor < 1.0) {
                issues.push({issue: `Attacking output low (${avgGoalsFor.toFixed(1)} GF/game) - need creative players`, severity: 'warning', icon: 'fa-gun'});
            }
        }

        // League position issues
        const teamPosition = league.teams.findIndex((t: any) => t.name === team.name) + 1;
        const leagueSize = league.teams.length;
        const ptsToRelegation = team.points - league.teams[leagueSize - 1].points;
        if (teamPosition > Math.ceil(leagueSize * 0.75) && ptsToRelegation < 5) {
            issues.push({issue: `In danger zone - relegation within reach`, severity: 'critical', icon: 'fa-triangle-exclamation'});
        }

        // Return top issues (max 4-5 depending on manager level)
        const maxIssues = accuracy >= 0.85 ? 5 : accuracy >= 0.7 ? 4 : 3;
        return issues.sort((a, b) => {
            const severityOrder = {critical: 0, warning: 1, info: 2};
            return severityOrder[a.severity] - severityOrder[b.severity];
        }).slice(0, maxIssues);
    }

    private static generatePlayerAlerts(team: TeamInLeague, accuracy: number): Array<{name: string; alert: string; type: 'morale' | 'fitness' | 'form' | 'injury'}> {
        const alerts: Array<{name: string; alert: string; type: 'morale' | 'fitness' | 'form' | 'injury'}> = [];

        if (accuracy >= 0.7) {
            // Morale alerts
            team.squad.filter(p => p.morale === 'Furious').forEach(p => {
                alerts.push({name: p.name, alert: 'Furious - likely transfer request incoming', type: 'morale'});
            });

            team.squad.filter(p => p.morale === 'Unhappy').slice(0, 2).forEach(p => {
                alerts.push({name: p.name, alert: 'Unhappy - consider wage increase or talk', type: 'morale'});
            });

            // Fitness alerts
            team.squad.filter(p => p.fitness < 25).slice(0, 2).forEach(p => {
                alerts.push({name: p.name, alert: `Dangerously fatigued (${p.fitness}%) - risk of injury`, type: 'fitness'});
            });

            // Form alerts (for starters in poor form)
            const starterIds = new Set(Object.values(team.tactics.lineup).filter(id => id !== null));
            team.squad.filter(p => (p.form || 0) < 1 && starterIds.has(p.id)).slice(0, 1).forEach(p => {
                alerts.push({name: p.name, alert: `In poor form as a starter - consider benching`, type: 'form'});
            });
        } else if (accuracy >= 0.5) {
            // Basic alerts for mid-level managers
            const furious = team.squad.find(p => p.morale === 'Furious');
            if (furious) {
                alerts.push({name: furious.name, alert: 'Furious morale', type: 'morale'});
            }
        }

        return alerts.slice(0, accuracy >= 0.85 ? 5 : 3);
    }

    private static generateTacticalRecommendations(team: TeamInLeague, accuracy: number): Array<{recommendation: string; reason: string}> {
        if (accuracy < 0.65) {
            return [];
        }

        const recommendations: Array<{recommendation: string; reason: string}> = [];
        const winRate = team.played > 0 ? team.won / team.played : 0;
        const drawRate = team.played > 0 ? team.drawn / team.played : 0;
        const avgGoalsFor = team.played > 0 ? team.goalsFor / team.played : 0;
        const avgGoalsAgainst = team.played > 0 ? team.goalsAgainst / team.played : 0;

        if (winRate > 0.6) {
            recommendations.push({recommendation: 'Maintain current system', reason: 'High win rate - keep doing what works'});
        } else if (drawRate > 0.4) {
            recommendations.push({recommendation: 'Shift to Attacking mentality', reason: 'Too many draws - need to push for wins'});
        }

        if (avgGoalsAgainst > 1.7) {
            recommendations.push({recommendation: 'Switch to Defensive formation', reason: `${avgGoalsAgainst.toFixed(1)} goals/game against - tighten defense`});
        }

        if (avgGoalsFor < 1.0 && team.lost > team.won) {
            recommendations.push({recommendation: 'Add attacking midfielders to lineup', reason: 'Low scoring output limiting wins'});
        }

        if (accuracy >= 0.85) {
            // Higher level managers give advanced tactical advice
            const excellentForm = team.squad.filter(p => (p.form || 0) > 2).length;
            if (excellentForm > team.squad.length * 0.4) {
                recommendations.push({recommendation: 'Exploit excellent squad form with aggressive tactics', reason: 'Squad in elite form - capitalize with bold approach'});
            }
        }

        return recommendations.slice(0, 3);
    }

    private static generateTransferTargets(team: TeamInLeague, accuracy: number): Array<{position: string; reasoning: string}> {
        if (accuracy < 0.7) {
            return [];
        }

        const targets: Array<{position: string; reasoning: string}> = [];

        // Identify weaknesses
        const avgGoalsFor = team.played > 0 ? team.goalsFor / team.played : 0;
        const avgGoalsAgainst = team.played > 0 ? team.goalsAgainst / team.played : 0;
        const midfielders = team.squad.filter(p => p.position === 'MID').length;
        const defenders = team.squad.filter(p => p.position === 'DEF').length;

        if (avgGoalsFor < 1.1) {
            targets.push({position: 'Attacking Midfielder / Winger', reasoning: `Low goals/game (${avgGoalsFor.toFixed(1)}) - need creative attackers`});
        }
        if (avgGoalsAgainst > 1.5) {
            targets.push({position: 'Defender', reasoning: `Conceding ${avgGoalsAgainst.toFixed(1)} goals/game - strengthen defense`});
        }
        if (midfielders < 6) {
            targets.push({position: 'Midfielder', reasoning: 'Light on midfield options - add depth'});
        }

        return targets.slice(0, accuracy >= 0.85 ? 3 : 2);
    }

    private static generateFixtureDifficulty(gameState: GameState): {difficulty: 'easy' | 'moderate' | 'hard'; summary: string} {
        // This is a simplified version - in a real scenario would check actual upcoming fixtures
        const randomValue = Math.random();
        if (randomValue < 0.3) {
            return {difficulty: 'easy', summary: 'Next 3 games are against weaker opposition - good opportunity for points'};
        } else if (randomValue < 0.7) {
            return {difficulty: 'moderate', summary: 'Mixed fixtures ahead - both winnable and challenging matchups'};
        } else {
            return {difficulty: 'hard', summary: 'Tough run of fixtures coming - need to be tactically sharp'};
        }
    }

    private static generateSquadCondition(team: TeamInLeague): string {
        const avgFitness = team.squad.reduce((sum, p) => sum + p.fitness, 0) / team.squad.length;
        const tiredPlayers = team.squad.filter(p => p.fitness < 50).length;
        const exhaustedPlayers = team.squad.filter(p => p.fitness < 30).length;

        if (avgFitness > 85) {
            return `Squad fresh (${Math.round(avgFitness)}% avg). Push hard this week.`;
        } else if (avgFitness > 70) {
            return `Squad match-fit (${Math.round(avgFitness)}% avg). Ready for battle.`;
        } else if (avgFitness > 50) {
            return `Moderate fatigue (${Math.round(avgFitness)}% avg). ${tiredPlayers} players tired - consider rotation.`;
        } else {
            return `Critical fatigue (${Math.round(avgFitness)}% avg). ${exhaustedPlayers} players at injury risk. Rotate immediately.`;
        }
    }

    private static generateInjuryReport(team: TeamInLeague): string {
        const severeCount = team.squad.filter(p => p.injury?.type === 'Severe').length;
        const lightCount = team.squad.filter(p => p.injury?.type === 'Light').length;

        if (severeCount === 0 && lightCount === 0) {
            return 'No injuries. Fully fit squad.';
        } else if (severeCount > 0) {
            return `${severeCount} out long-term${lightCount > 0 ? ` + ${lightCount} minor injuries` : ''}`;
        } else {
            return `${lightCount} light injur${lightCount === 1 ? 'y' : 'ies'} - monitor closely`;
        }
    }

    private static generateFormWatch(team: TeamInLeague): string {
        const excellent = team.squad.filter(p => (p.form || 0) > 3).length;
        const good = team.squad.filter(p => (p.form || 0) >= 1 && (p.form || 0) <= 3).length;
        const poor = team.squad.filter(p => (p.form || 0) < 1).length;

        if (excellent > good && excellent > poor) {
            return `Strong form across squad (${excellent} excellent, ${poor} poor)`;
        } else if (good > excellent && good > poor) {
            return `Solid form (${good} in good form, ${poor} struggling)`;
        } else if (poor > excellent) {
            return `Form issues (${poor} poor form - needs attention)`;
        } else {
            return `Mixed form (${excellent} excellent, ${good} good, ${poor} poor)`;
        }
    }

    private static getTopFormers(team: TeamInLeague): string[] {
        return team.squad
            .filter(p => p.form && p.form > 3)
            .sort((a, b) => (b.form || 0) - (a.form || 0))
            .slice(0, 3)
            .map(p => p.name);
    }

    private static generateFinances(team: TeamInLeague): string {
        const balance = team.finances.balance;
        if (balance > 10000000) {
            return `Strong position (£${(balance/1000000).toFixed(1)}m) - can invest strategically`;
        } else if (balance > 2000000) {
            return `Healthy (£${(balance/1000000).toFixed(1)}m) - room for transfers`;
        } else if (balance > 0) {
            return `Tight (£${(balance/1000000).toFixed(1)}m) - be cautious with spending`;
        } else {
            return `Deficit (£${(balance/1000000).toFixed(1)}m) - cannot spend, need revenue`;
        }
    }

    private static getManagerAdvice(team: TeamInLeague, accuracy: number): string {
        const avgForm = team.squad.reduce((sum, p) => sum + (p.form || 0), 0) / team.squad.length;
        const avgMorale = this.calculateAverageMorale(team.squad);
        const avgFitness = team.squad.reduce((sum, p) => sum + p.fitness, 0) / team.squad.length;
        const furiousPlayers = team.squad.filter(p => p.morale === 'Furious').length;

        if (accuracy >= 0.9) {
            // Elite managers give specific, actionable advice
            if (furiousPlayers > 0) {
                return `${furiousPlayers} player(s) furious - offer contracts/wages immediately or prepare for transfer request.`;
            } else if (avgMorale < 2.5) {
                return 'Morale slipping across squad. Individual meetings will rebuild confidence faster than anything else.';
            } else if (avgFitness < 40) {
                return 'Squad dangerously fatigued. Blood youth players or rest fringe stars - prevent injuries now.';
            } else if (avgForm > 2.5) {
                return 'Team in elite form. This is the moment for tactical boldness - exploit opposition with aggressive play.';
            } else if (team.won < team.lost && team.played >= 5) {
                return 'More losses than wins. System needs change - formation, mentality, or lineup adjustment required immediately.';
            } else {
                return 'Focus on marginal gains. Small improvements compound - 1% better fitness, 2% better tactics = wins.';
            }
        } else if (accuracy >= 0.65) {
            // Mid-level managers give contextual advice
            const contextualAdvice = [
                avgForm > 1.5 ? 'Form is good - maintain consistency with unchanged lineups.' : 'Form is patchy - wins rebuild confidence.',
                avgMorale > 3 ? 'Morale is high - capitalize with bold tactics.' : 'Morale needs attention - address issues soon.',
                avgFitness < 50 ? 'Fatigue is real - use rotation carefully.' : 'Squad is fresh - press your advantages.',
                team.won > team.lost ? 'Winning breeds confidence - maintain momentum.' : 'Change something small - it might shift momentum.',
            ];
            return contextualAdvice[Math.floor(Math.random() * contextualAdvice.length)];
        } else {
            // Low-level managers give generic advice
            const basicAdvice = [
                'Try to win more matches.',
                'Happy players perform better.',
                'Defense is important.',
                'Use all your players, not just stars.',
            ];
            return basicAdvice[Math.floor(Math.random() * basicAdvice.length)];
        }
    }

    private static getOrdinal(num: number): string {
        const j = num % 10;
        const k = num % 100;
        if (j === 1 && k !== 11) return num + 'st';
        if (j === 2 && k !== 12) return num + 'nd';
        if (j === 3 && k !== 13) return num + 'rd';
        return num + 'th';
    }

    private static calculateAverageMorale(squad: any[]): number {
        const moraleValues: Record<string, number> = {
            'Ecstatic': 5, 'Happy': 4, 'Content': 3, 'Unhappy': 2, 'Furious': 1
        };
        const total = squad.reduce((sum, p) => sum + (moraleValues[p.morale] || 3), 0);
        return total / Math.max(1, squad.length);
    }
}
